export interface CoreMetricsData {
  engagementRate: string;
  followerGrowth: string;
  reach: string;
  impressions: string;
  ctr: string;
  hashtagPerformance: { hashtag: string; value: string }[];
  engagementRateHistory: { name: string; value: number }[];
  followerGrowthHistory: { name: string; value: number }[];
  platformPerformance: { platform: string; followers: number; engagementRate: number; lastPostDate?: string }[];
  platformEngagementHistory: PlatformData[];
  platformFollowerGrowthHistory: PlatformData[];
  missingPlatforms?: { name: string; reason: string }[];
  dataAvailability?: {
    hasHistoricalData: boolean;
    availablePeriods: number;
    requestedPeriods: number;
    message?: string;
  };
}

export interface PlatformData {
    name: string;
    data: { name: string; value: number }[];
}

export interface AudienceAnalyticsData {
  demographics: { age: string; gender: string; location: string };
  activeHours: { hour: string; engagement: number }[];
  sentimentBreakdown: { positive: number; neutral: number; negative: number };
  audienceDensityByCountry: { country: string; value: number }[];
  platformSentimentBreakdown: PlatformSentimentData[];
  platformAudienceDensityByCountry: PlatformAudienceDensityData[];
  // New comprehensive metrics
  platforms?: PlatformAudienceData[];
  combined_metrics?: CombinedAudienceMetrics;
  summary?: AudienceSummary;
}

export interface PlatformAudienceData {
  platform: string;
  demographics: { age: string; gender: string; location: string; active_commenters?: number };
  active_hours: { hour: string; engagement: number }[];
  sentiment_breakdown: { positive: number; neutral: number; negative: number };
  audience_by_country: { country: string; value: number }[];
  engagement_patterns?: {
    avg_likes_per_post?: number;
    avg_comments_per_post?: number;
    avg_shares_per_post?: number;
    total_engagement?: number;
    best_performing_metric?: string;
  };
  content_performance?: {
    top_posts: any[];
    avg_performance: number;
    total_posts_analyzed?: number;
  };
  audience_growth_trend?: string;
  interaction_types?: { [key: string]: number };
  peak_engagement_days?: string[];
  total_comments_analyzed: number;
  total_posts_analyzed: number;
  status?: string;
}

export interface CombinedAudienceMetrics {
  total_comments_analyzed: number;
  total_posts_analyzed: number;
  overall_sentiment: { positive: number; neutral: number; negative: number };
  combined_active_hours: { hour: string; engagement: number }[];
  top_performing_platforms: {
    platform: string;
    score: number;
    comments: number;
    posts: number;
  }[];
  audience_insights: {
    total_unique_commenters: number;
    engagement_trends: string;
    peak_days: string[];
  };
}

export interface AudienceSummary {
  total_platforms_connected: number;
  total_platforms_analyzed: number;
  total_comments_analyzed: number;
  total_posts_analyzed: number;
  top_performing_platform: string;
}

export interface PlatformSentimentData {
  platform: string;
  data: { name: string; value: number }[];
}

export interface PlatformAudienceDensityData {
  platform: string;
  data: { country: string; value: number }[];
}

import api from './api';

export const fetchCoreMetrics = async (dateRange: '7d' | '1m' | '3m' = '1m'): Promise<CoreMetricsData> => {
  const monthCount = getMonthCountFromRange(dateRange);
  const days = dateRange === '7d' ? 7 : monthCount * 30; // Use exact days for 7d filter
  const isDailyView = dateRange === '7d'; // Use daily view for 7 days
  
  if (process.env.NODE_ENV === 'development') {
    console.log(`📊 fetchCoreMetrics called with dateRange: ${dateRange}, days: ${days}, isDailyView: ${isDailyView}`);
    console.log(`📊 Time period configuration: ${isDailyView ? 'Daily view (7 days)' : `Monthly view (${monthCount} months)`}`);
  }
  
  if (monthCount > 1) {
    console.log(`⚠️ WARNING: Requested ${monthCount} months but limiting to current month due to limited historical data`);
  }
  
  // Try to use historical data first, then fall back to real-time analytics
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Attempting to fetch historical data from database...');
  }
  
  try {
    // First, try to get historical data from the database
    if (process.env.NODE_ENV === 'development') {
      console.log(`📊 Fetching historical data for ${days} days...`);
    }
    let historicalData = null;
    try {
      const historicalResponse = await api.get(`/social/historical-metrics?days=${days}`);
      historicalData = historicalResponse.data;
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Historical data received:', historicalData);
        console.log('📊 Historical data total records:', historicalData?.total_records);
        console.log('📊 Historical data raw records:', historicalData?.raw_records);
      }
    } catch (histError) {
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 ERROR fetching historical data:', histError);
        console.log('📊 Will use current analytics instead');
      }
    }
    
    // Also get current analytics data
    if (process.env.NODE_ENV === 'development') {
      console.log(`📊 Fetching real-time analytics data for ${days} days...`);
    }
    const response = await api.get(`/social/analytics?days=${days}`);
    const analyticsData = response.data;
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Raw analytics data received:', analyticsData);
    }
    
    if (analyticsData.platforms && analyticsData.platforms.length > 0) {
      // Use real data from connected platforms
      const combined = analyticsData.combined_metrics;
      
      // Check if we have historical data to use for charts
      const hasHistoricalData = historicalData && historicalData.total_records > 0;
      console.log(`📊 Historical data available: ${hasHistoricalData}, Records: ${historicalData?.total_records || 0}`);
      
      if (hasHistoricalData) {
        console.log('📊 ✅ USING HISTORICAL DATABASE DATA for charts');
      } else {
        console.log('📊 ❌ FALLING BACK TO GENERATED DATA - no historical data available');
      }
      
      // Calculate CTR (placeholder - would need click data from platforms)
      const estimatedCTR = combined.avg_engagement_rate * 0.1; // Rough estimate
      
      // Check if this is a new account with minimal data
      const totalFollowers = analyticsData.platforms.reduce((sum, p) => sum + (p.followers || p.follower_count || 0), 0);
      const totalPosts = analyticsData.platforms.reduce((sum, p) => sum + (p.total_posts_analyzed || 0), 0);
      const isNewAccount = totalFollowers <= 5 && totalPosts <= 10;
      
      if (isNewAccount) {
        console.log('📊 New account detected - showing realistic minimal data');
        
        // Show realistic data for new accounts (with caps)
        const cappedEngagementNew = Math.min(combined.avg_engagement_rate || 0, 20.0);
        const cappedGrowthNew = Math.min(combined.avg_follower_growth || 0, 100.0);
        
        return {
          engagementRate: cappedEngagementNew > 0 ? `${cappedEngagementNew.toFixed(2)}%` : '0.00%',
          followerGrowth: cappedGrowthNew > 0 ? `${cappedGrowthNew.toFixed(2)}%` : '0.00%',
          reach: combined.total_reach > 0 ? combined.total_reach.toLocaleString() : '0',
          impressions: combined.total_impressions > 0 ? combined.total_impressions.toLocaleString() : '0',
          ctr: '0.00%',
          
          // Show realistic hashtags for new accounts
          hashtagPerformance: [
            { hashtag: '#newaccount', value: '0' },
            { hashtag: '#juststarted', value: '0' },
            { hashtag: '#firstpost', value: '0' },
          ],
          
          // Use real historical data if available, otherwise generate from insights
          engagementRateHistory: (() => {
            if (hasHistoricalData) {
              console.log('📊 Using database data for engagement rate history');
              const dbHistory = generateHistoryFromDatabase(historicalData, 'engagement_rate', isDailyView);
              console.log('📊 Database history result:', dbHistory);
              if (dbHistory.length > 0) return dbHistory;
              console.log('📊 Database history was empty, falling back to insights');
            }
            console.log('📊 Using insights data for engagement rate history');
            const insightsHistory = generateHistoryFromInsights(analyticsData.platforms, 'engagement_rate', monthCount, isDailyView);
            console.log('📊 Insights history result:', insightsHistory);
            return insightsHistory;
          })(),
          followerGrowthHistory: (() => {
            if (hasHistoricalData) {
              console.log('📊 Using database data for follower growth history');
              const dbHistory = generateHistoryFromDatabase(historicalData, 'follower_growth', isDailyView);
              if (dbHistory.length > 0) return dbHistory;
              console.log('📊 Database history was empty, falling back to insights');
            }
            console.log('📊 Using insights data for follower growth history');
            return generateHistoryFromInsights(analyticsData.platforms, 'follower_growth', monthCount, isDailyView);
          })(),
          
          // Show real platform data even if minimal
          platformPerformance: analyticsData.platforms.map(platform => ({
            platform: platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1),
            followers: platform.followers || platform.follower_count || 0,
            engagementRate: platform.engagement_rate || 0,
            lastPostDate: platform.last_post_date || platform.latest_post_date || null
          })),
          
          // Use the improved history generation
          platformEngagementHistory: hasHistoricalData ?
            generatePlatformHistoryFromDatabase(historicalData, 'engagement_rate', isDailyView) :
            analyticsData.platforms.map(platform => ({
              name: platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1),
              data: generateMonthlyHistory(platform.engagement_rate || 0, 'engagement', monthCount, platform, isDailyView)
            })),
          
          platformFollowerGrowthHistory: hasHistoricalData ?
            generatePlatformHistoryFromDatabase(historicalData, 'follower_growth', isDailyView) :
            analyticsData.platforms.map(platform => ({
              name: platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1),
              data: generateMonthlyHistory(platform.follower_growth || 0, 'growth', monthCount, platform, isDailyView)
            }))
        };
      }
      
      // Apply safety caps to prevent unrealistic values
      const safeEngagement = Math.min(Math.max(combined.avg_engagement_rate || 0, 0), 15.0);
      const safeGrowth = Math.min(Math.max(combined.avg_follower_growth || 0, 0), 50.0);
      const safeCTR = Math.min(safeEngagement * 0.08, 5.0); // More realistic CTR
      
      return {
        engagementRate: `${safeEngagement.toFixed(2)}%`,
        followerGrowth: `${safeGrowth.toFixed(2)}%`,
        reach: combined.total_reach.toLocaleString(),
        impressions: combined.total_impressions.toLocaleString(),
        ctr: `${safeCTR.toFixed(2)}%`,
        // Generate mock hashtag performance (would need separate API for this)
        hashtagPerformance: [
          { hashtag: '#socialmedia', value: '350' },
          { hashtag: '#marketing', value: '180' },
          { hashtag: '#digitalads', value: '75' },
        ],
        
        // Generate engagement rate history from database or insights data
        engagementRateHistory: hasHistoricalData ? 
          generateHistoryFromDatabase(historicalData, 'engagement_rate', isDailyView) :
          generateHistoryFromInsights(analyticsData.platforms, 'engagement_rate', monthCount, isDailyView),
        
        // Generate follower growth history from database or insights data
        followerGrowthHistory: hasHistoricalData ? 
          generateHistoryFromDatabase(historicalData, 'follower_growth', isDailyView) :
          generateHistoryFromInsights(analyticsData.platforms, 'follower_growth', monthCount, isDailyView),
        
        // Convert platform data to expected format with real follower counts
        platformPerformance: analyticsData.platforms.map(platform => {
          console.log(`📊 API Platform ${platform.platform} raw data:`, {
            followers: platform.followers,
            follower_count: platform.follower_count,
            engagement_rate: platform.engagement_rate,
            last_post_date: platform.last_post_date,
            latest_post_date: platform.latest_post_date,
            recent_posts: platform.recent_posts?.length || 0
          });
          
          return {
            platform: platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1),
            followers: platform.followers || platform.follower_count || 0,
            engagementRate: platform.engagement_rate || 0,
            lastPostDate: platform.last_post_date || platform.latest_post_date || null
          };
        }),
        
        // Generate platform history data from database or current data
        platformEngagementHistory: hasHistoricalData ?
          generatePlatformHistoryFromDatabase(historicalData, 'engagement_rate', isDailyView) :
          analyticsData.platforms.map(platform => {
            console.log(`📊 Generating engagement history for ${platform.platform}:`, {
              current_engagement_rate: platform.engagement_rate,
              follower_count: platform.followers || platform.follower_count,
              posts_analyzed: platform.total_posts_analyzed
            });
            return {
              name: platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1),
              data: generateMonthlyHistory(platform.engagement_rate || 0, 'engagement', monthCount, platform, isDailyView)
            };
          }),
        
        platformFollowerGrowthHistory: hasHistoricalData ?
          generatePlatformHistoryFromDatabase(historicalData, 'follower_growth', isDailyView) :
          analyticsData.platforms.map(platform => {
            console.log(`📊 Generating follower growth history for ${platform.platform}:`, {
              current_follower_growth: platform.follower_growth,
              follower_count: platform.followers || platform.follower_count,
              posts_analyzed: platform.total_posts_analyzed
            });
            return {
              name: platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1),
              data: generateMonthlyHistory(platform.follower_growth || 0, 'growth', monthCount, platform, isDailyView)
            };
          }),
        
        // Add info about missing platforms (if needed in the future)
        missingPlatforms: [],
        
        // Add data availability info
        dataAvailability: {
          hasHistoricalData: hasHistoricalData,
          availablePeriods: hasHistoricalData ? historicalData.total_records : (isDailyView ? 2 : 1),
          requestedPeriods: isDailyView ? 7 : monthCount,
          message: hasHistoricalData ? 
            undefined :
            (analyticsData.platforms.every(p => (p.followers || p.follower_count || 0) === 0) ?
              '📊 No followers yet - connect your accounts and start posting to see analytics' :
              (isDailyView ? 
                '⚠️ No historical data - showing current period only' : 
                monthCount > 1 ? '⚠️ No historical data - showing current month only' : undefined))
        }
      };
    }
  } catch (error) {
    console.error('📊 Error fetching real analytics data:', error);
    console.log('📊 Using fallback mock data - no connected accounts or API error');
  }
  
  // Fallback to mock data if no real data available
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        engagementRate: '15.23%',
        followerGrowth: '2.5%',
        reach: '5000',
        impressions: '25000',
        ctr: '5.12%',
        hashtagPerformance: [
          { hashtag: '#socialmedia', value: '350' },
          { hashtag: '#marketing', value: '180' },
          { hashtag: '#digitalads', value: '75' },
        ],
        engagementRateHistory: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).map((period, index) => ({
          name: period,
          value: 12 + index * 0.5 + Math.random() * 2
        })),
        followerGrowthHistory: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).map((period, index) => ({
          name: period,
          value: 1.5 + index * 0.15 + Math.random() * 0.3
        })),
        platformPerformance: [
          { platform: 'Facebook', engagement: 70, reach: 15000 },
          { platform: 'Instagram', engagement: 85, reach: 20000 },
          { platform: 'Twitter', engagement: 50, reach: 10000 },
          { platform: 'LinkedIn', engagement: 60, reach: 12000 },
          { platform: 'TikTok', engagement: 90, reach: 25000 },
          { platform: 'YouTube', engagement: 75, reach: 18000 },
        ],
        platformEngagementHistory: [
            { name: 'Facebook', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 10 + i * 0.8 + Math.random() * 2 })) },
            { name: 'Instagram', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 15 + i * 0.8 + Math.random() * 3 })) },
            { name: 'Twitter', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 5 + i * 0.8 + Math.random() * 2 })) },
            { name: 'LinkedIn', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 8 + i * 0.8 + Math.random() * 2 })) },
            { name: 'TikTok', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 20 + i * 2 + Math.random() * 3 })) },
            { name: 'YouTube', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 12 + i * 1.5 + Math.random() * 2 })) },
        ],
        platformFollowerGrowthHistory: [
            { name: 'Facebook', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 1 + i * 0.1 + Math.random() * 0.2 })) },
            { name: 'Instagram', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 2 + i * 0.2 + Math.random() * 0.3 })) },
            { name: 'Twitter', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 0.5 + i * 0.1 + Math.random() * 0.1 })) },
            { name: 'LinkedIn', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 0.8 + i * 0.1 + Math.random() * 0.1 })) },
            { name: 'TikTok', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 3 + i * 0.4 + Math.random() * 0.5 })) },
            { name: 'YouTube', data: (isDailyView ? getCurrentDays(7) : getCurrentMonths(1)).filter(period => {
              const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return period !== today;
            }).map((period, i) => ({ name: period, value: 1.5 + i * 0.2 + Math.random() * 0.3 })) },
        ],
        
        // Add data availability info for mock data
        dataAvailability: {
          hasHistoricalData: false,
          availablePeriods: isDailyView ? 7 : 1,
          requestedPeriods: isDailyView ? 7 : monthCount,
          message: monthCount > 1 ? 'Mock data - showing current month only' : undefined
        }
      });
    }, 500);
  });
};

// Build metrics from real historical data
function buildMetricsFromHistoricalData(historicalData: any, monthCount: number, isDailyView: boolean = false): CoreMetricsData {
  console.log('📊 Building metrics from real historical data:', historicalData);
  
  // Our historical data format: { total_records: 2, raw_records: [...] }
  const rawRecords = historicalData.raw_records || [];
  
  if (rawRecords.length === 0) {
    throw new Error('No historical records found');
  }
  
  // Group records by platform
  const platformData: { [key: string]: any[] } = {};
  rawRecords.forEach((record: any) => {
    if (!platformData[record.platform]) {
      platformData[record.platform] = [];
    }
    platformData[record.platform].push(record);
  });
  
  // Calculate current metrics from latest data points
  let totalFollowers = 0;
  let totalEngagement = 0;
  let platformCount = 0;
  
  const platformPerformance = [];
  const platformEngagementHistory = [];
  const platformFollowerGrowthHistory = [];
  
  for (const [platform, records] of Object.entries(platformData)) {
    if (records.length > 0) {
      // Sort records by date to get latest
      const sortedRecords = records.sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime());
      const latestRecord = sortedRecords[sortedRecords.length - 1];
      
      totalFollowers += latestRecord.followers_count || 0;
      totalEngagement += latestRecord.engagement_rate || 0;
      platformCount++;
      
      // Add to platform performance
      platformPerformance.push({
        platform: platform.charAt(0).toUpperCase() + platform.slice(1),
        followers: latestRecord.followers_count || 0,
        engagementRate: latestRecord.engagement_rate || 0,
        lastPostDate: latestRecord.recorded_at
      });
      
      // Convert historical records to chart data (daily or monthly)
      const monthlyEngagement = convertHistoricalRecordsToMonthly(sortedRecords, monthCount, 'engagement_rate', false, isDailyView);
      const monthlyFollowerGrowth = convertHistoricalRecordsToMonthly(sortedRecords, monthCount, 'followers_count', true, isDailyView);
      
      platformEngagementHistory.push({
        name: platform.charAt(0).toUpperCase() + platform.slice(1),
        data: monthlyEngagement
      });
      
      platformFollowerGrowthHistory.push({
        name: platform.charAt(0).toUpperCase() + platform.slice(1),
        data: monthlyFollowerGrowth
      });
    }
  }
  
  const avgEngagement = platformCount > 0 ? totalEngagement / platformCount : 0;
  
  // Calculate follower growth from first to last record
  let overallFollowerGrowth = 0;
  if (rawRecords.length >= 2) {
    const sortedRecords = rawRecords.sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime());
    const firstTotal = sortedRecords.slice(0, platformCount).reduce((sum, r) => sum + (r.followers_count || 0), 0);
    const lastTotal = sortedRecords.slice(-platformCount).reduce((sum, r) => sum + (r.followers_count || 0), 0);
    
    if (firstTotal > 0) {
      overallFollowerGrowth = ((lastTotal - firstTotal) / firstTotal) * 100;
    }
  }
  
  return {
    engagementRate: `${avgEngagement.toFixed(2)}%`,
    followerGrowth: `${overallFollowerGrowth.toFixed(2)}%`,
    reach: Math.round(totalFollowers * 2.5).toLocaleString(), // Estimated reach
    impressions: Math.round(totalFollowers * 5).toLocaleString(), // Estimated impressions
    ctr: `${(avgEngagement * 0.1).toFixed(2)}%`, // Estimated CTR
    hashtagPerformance: [
      { hashtag: '#socialmedia', value: '350' },
      { hashtag: '#marketing', value: '180' },
      { hashtag: '#content', value: '75' },
    ],
    engagementRateHistory: convertHistoricalRecordsToMonthly(rawRecords, monthCount, 'engagement_rate', false, isDailyView),
    followerGrowthHistory: convertHistoricalRecordsToMonthly(rawRecords, monthCount, 'followers_count', true, isDailyView),
    platformPerformance,
    platformEngagementHistory,
    platformFollowerGrowthHistory
  };
}

// Convert historical records to chart data (daily for 7d, monthly for others)
function convertHistoricalRecordsToMonthly(records: any[], monthCount: number, field: string, isGrowth: boolean = false, isDailyView: boolean = false) {
  
  // Helper function to add small offset to prevent overlapping lines
  const addOffsetForOverlap = (data: any[], platformIndex: number = 0) => {
    return data.map(item => ({
      ...item,
      value: item.value === 0 ? platformIndex * 0.01 : item.value // Add tiny offset for zero values
    }));
  };
  console.log(`📊 Converting ${records.length} records for ${field}, isGrowth: ${isGrowth}, months: ${monthCount}, isDailyView: ${isDailyView}`);
  
  if (records.length === 0) {
    if (isDailyView) {
      return getCurrentDays(7).map(day => ({ name: day, value: 0 }));
    } else {
      return getCurrentMonths(monthCount).map(month => ({ name: month, value: 0 }));
    }
  }
  
  // Sort records by date
  const sortedRecords = records.sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime());
  console.log(`📊 Sorted records:`, sortedRecords.map(r => ({ date: r.recorded_at, value: r[field] })));
  
  if (isDailyView) {
    // For 7-day view, show daily data points
    const days = getCurrentDays(7);
    const dataByDay: { [key: string]: number[] } = {};
    
    sortedRecords.forEach(record => {
      const date = new Date(record.recorded_at);
      const dayKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      if (!dataByDay[dayKey]) {
        dataByDay[dayKey] = [];
      }
      
      let value = record[field] || 0;
      
      // For growth calculation
      if (isGrowth) {
        const recordIndex = sortedRecords.findIndex(r => r.recorded_at === record.recorded_at);
        if (recordIndex > 0) {
          const prevValue = sortedRecords[recordIndex - 1][field] || 0;
          value = prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : 0;
        } else {
          value = 0; // First record has no growth
        }
      }
      
      dataByDay[dayKey].push(value);
    });
    
    // Only show days with actual data (no interpolation)
    const result = days.map((day, index) => {
      if (dataByDay[day] && dataByDay[day].length > 0) {
        // Use actual data for this day
        const average = dataByDay[day].reduce((sum, val) => sum + val, 0) / dataByDay[day].length;
        return { name: day, value: Math.max(0, average) };
      } else {
        // No data for this day - show null (chart will skip these points)
        return { name: day, value: null };
      }
    }).filter(item => item.value !== null); // Remove days without data
    
    console.log('📊 Generated daily chart data:', result);
    return result;
  }
  
  // Monthly view logic
  const months = getCurrentMonths(monthCount);
  
  // If we have actual historical data points, use them
  if (sortedRecords.length >= 2) {
    console.log('📊 Using real historical data points for chart');
    
    // Map historical data to months
    const dataByMonth: { [key: string]: number[] } = {};
    
    sortedRecords.forEach(record => {
      const date = new Date(record.recorded_at);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
      
      if (!dataByMonth[monthKey]) {
        dataByMonth[monthKey] = [];
      }
      
      let value = record[field] || 0;
      
      // For growth calculation
      if (isGrowth) {
        const recordIndex = sortedRecords.findIndex(r => r.recorded_at === record.recorded_at);
        if (recordIndex > 0) {
          const prevValue = sortedRecords[recordIndex - 1][field] || 0;
          value = prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : 0;
        } else {
          value = 0; // First record has no growth
        }
      }
      
      dataByMonth[monthKey].push(value);
    });
    
    // Only show months with actual data (no interpolation)
    const result = months.map((month, index) => {
      if (dataByMonth[month] && dataByMonth[month].length > 0) {
        // Use actual data for this month
        const average = dataByMonth[month].reduce((sum, val) => sum + val, 0) / dataByMonth[month].length;
        return { name: month, value: Math.max(0, average) };
      } else {
        // No data for this month - show null (chart will skip these points)
        return { name: month, value: null };
      }
    }).filter(item => item.value !== null); // Remove months without data
    
    console.log('📊 Generated chart data:', result);
    return result;
  }
  
  // Fallback for limited data
  const currentValue = sortedRecords[sortedRecords.length - 1][field] || 0;
  console.log(`📊 Limited data fallback - Current ${field} value:`, currentValue);
  
  return months.map((month, index) => {
    const progress = (index + 1) / months.length;
    let value;
    
    if (isGrowth) {
      value = currentValue <= 5 ? (index >= months.length - 2 ? Math.random() * 0.5 : 0) : currentValue * progress * 0.3;
    } else {
      value = currentValue > 100 ? currentValue * progress * 0.8 : currentValue * progress;
    }
    
    return {
      name: month,
      value: Math.max(0, value)
    };
  });
}

// Convert daily historical data to monthly averages for charts (legacy function)
function convertDailyToMonthly(dailyData: any[], monthCount: number, type: 'value' | 'growth' = 'value') {
  const months = getCurrentMonths(monthCount);
  const monthlyData = [];
  
  // Group daily data by month
  const dataByMonth: { [key: string]: number[] } = {};
  
  for (const dataPoint of dailyData) {
    const date = new Date(dataPoint.date);
    const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
    
    if (!dataByMonth[monthKey]) {
      dataByMonth[monthKey] = [];
    }
    
    const value = type === 'growth' ? dataPoint.growth_rate : dataPoint.value;
    dataByMonth[monthKey].push(value);
  }
  
  // Calculate monthly averages
  for (const month of months) {
    const monthData = dataByMonth[month] || [];
    const average = monthData.length > 0 
      ? monthData.reduce((sum, val) => sum + val, 0) / monthData.length 
      : 0;
    
    monthlyData.push({
      name: month,
      value: Math.max(0, average)
    });
  }
  
  return monthlyData;
}

// Calculate overall growth rate from historical data
function calculateOverallGrowth(platformsData: any, historyType: string): number {
  let totalGrowth = 0;
  let platformCount = 0;
  
  for (const platform of Object.values(platformsData)) {
    const history = (platform as any)[historyType];
    if (history && history.length >= 2) {
      const firstValue = history[0].value;
      const lastValue = history[history.length - 1].value;
      
      if (firstValue > 0) {
        const growth = ((lastValue - firstValue) / firstValue) * 100;
        totalGrowth += growth;
        platformCount++;
      }
    }
  }
  
  return platformCount > 0 ? totalGrowth / platformCount : 0;
}

// Helper function to get current month names based on date range
function getCurrentMonths(count: number = 6): string[] {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const result = [];
  
  for (let i = count - 1; i >= 0; i--) {
    const monthIndex = (currentMonth - i + 12) % 12;
    result.push(months[monthIndex]);
  }
  
  return result;
}

// Helper function to get current day names for daily view (excluding today)
function getCurrentDays(count: number = 7): string[] {
  const result = [];
  const currentDate = new Date();
  
  // Start from yesterday (i+1) since today's data isn't collected yet
  for (let i = count; i >= 1; i--) {
    const date = new Date(currentDate);
    date.setDate(currentDate.getDate() - i);
    const dayKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    result.push(dayKey);
  }
  
  return result;
}

// Helper function to get month count from date range
function getMonthCountFromRange(dateRange: '7d' | '1m' | '3m'): number {
  switch (dateRange) {
    case '7d': return 1; // Show 1 month of data for 7 days
    case '1m': return 1;
    case '3m': return 3;
    default: return 1;
  }
}

// Helper functions for processing real database historical data
function generateHistoryFromDatabase(historicalData: any, metric: string, isDailyView: boolean = false) {
  console.log(`📊 Generating ${metric} history from database data:`, historicalData);
  console.log(`📊 Raw records count: ${historicalData?.raw_records?.length || 0}`);
  
  if (!historicalData || !historicalData.raw_records || historicalData.raw_records.length === 0) {
    console.log('📊 ❌ No raw records available for database history generation');
    return [];
  }
  
  const records = historicalData.raw_records;
  const dataByDate: { [key: string]: number[] } = {};
  
  // Group records by date
  records.forEach((record: any) => {
    const date = new Date(record.recorded_at);
    const dateKey = isDailyView ? 
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) :
      date.toLocaleDateString('en-US', { month: 'short' });
    
    if (!dataByDate[dateKey]) {
      dataByDate[dateKey] = [];
    }
    
    const value = metric === 'engagement_rate' ? record.engagement_rate : 
                  metric === 'follower_growth' ? calculateFollowerGrowth(records, record) : 0;
    
    dataByDate[dateKey].push(value || 0);
  });
  
  // Convert to chart format
  return Object.entries(dataByDate).map(([date, values]) => ({
    name: date,
    value: values.reduce((sum, val) => sum + val, 0) / values.length // Average for the period
  })).sort((a, b) => {
    // Sort by date
    const dateA = new Date(a.name + ', 2024');
    const dateB = new Date(b.name + ', 2024');
    return dateA.getTime() - dateB.getTime();
  });
}

function generatePlatformHistoryFromDatabase(historicalData: any, metric: string, isDailyView: boolean = false) {
  console.log(`📊 Generating platform ${metric} history from database data`);
  
  if (!historicalData || !historicalData.platforms) {
    return [];
  }
  
  return Object.entries(historicalData.platforms).map(([platform, data]: [string, any]) => {
    const historyKey = metric === 'engagement_rate' ? 'engagement_history' : 'follower_history';
    const history = data[historyKey] || [];
    
    const chartData = history.map((item: any) => {
      const date = new Date(item.date);
      const dateKey = isDailyView ? 
        date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) :
        date.toLocaleDateString('en-US', { month: 'short' });
      
      return {
        name: dateKey,
        value: item.value || 0
      };
    });
    
    return {
      name: platform.charAt(0).toUpperCase() + platform.slice(1),
      data: chartData
    };
  });
}

function calculateFollowerGrowth(allRecords: any[], currentRecord: any): number {
  // Find previous record for the same platform
  const platformRecords = allRecords
    .filter(r => r.platform === currentRecord.platform)
    .sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime());
  
  const currentIndex = platformRecords.findIndex(r => r.recorded_at === currentRecord.recorded_at);
  
  if (currentIndex <= 0) return 0; // No previous record
  
  const previousRecord = platformRecords[currentIndex - 1];
  const currentFollowers = currentRecord.followers_count || 0;
  const previousFollowers = previousRecord.followers_count || 0;
  
  if (previousFollowers === 0) return 0;
  
  return ((currentFollowers - previousFollowers) / previousFollowers) * 100;
}

// Helper functions for processing real analytics data
function generateHistoryFromInsights(platforms: any[], metric: string, monthCount: number = 6, isDailyView: boolean = false) {
  // For accounts with no followers/activity, don't show fake historical data
  const hasNoActivity = platforms.every(p => 
    (p.followers || p.follower_count || 0) === 0 && 
    (p.total_posts_analyzed || 0) === 0
  );
  
  if (hasNoActivity) {
    console.log('📊 No activity detected - returning empty data');
    return []; // Return empty array - no fake data
  }
  
  const isNewAccount = platforms.every(p => 
    (p.followers || p.follower_count || 0) <= 5 && 
    (p.total_posts_analyzed || 0) <= 10
  );
  
  if (isNewAccount) {
    console.log('📊 New account detected - showing minimal data only for current period');
    
    // For new accounts, be very conservative - only show yesterday (not today)
    if (isDailyView) {
      // Only show yesterday with minimal data if there's any activity
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      return [
        {
          name: yesterday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          value: metric === 'engagement_rate' ? 0.1 : 0.05 // Very minimal values
        }
      ];
    } else {
      // For monthly view, only show current month
      const currentMonth = getCurrentMonths(1);
      return currentMonth.map((month) => ({
        name: month,
        value: metric === 'engagement_rate' ? 0.1 : 0.05 // Very minimal values
      }));
    }
  }
  
  // For established accounts, still only show periods where we likely have data
  // Don't generate fake historical data for months we don't have
  const avgValue = platforms.length > 0 ? 
    platforms.reduce((sum, p) => sum + (p[metric] || 0), 0) / platforms.length : 0;
  
  let baseValue: number;
  let variation: number;
  let trend: number;
  
  if (metric === 'engagement_rate') {
    baseValue = Math.max(0.5, avgValue);
    variation = Math.max(0.3, avgValue * 0.4);
    trend = Math.max(0.02, avgValue * 0.02);
  } else if (metric === 'follower_growth') {
    baseValue = Math.max(0.1, avgValue);
    variation = Math.max(0.2, avgValue * 0.4);
    trend = Math.max(0.01, avgValue * 0.02);
  } else {
    baseValue = avgValue;
    variation = avgValue * 0.4;
    trend = avgValue * 0.02;
  }
  
  // For established accounts, limit the historical data to reasonable periods
  // Don't show 3 months of fake data if we only have recent data
  const limitedPeriods = isDailyView ? 
    getCurrentDays(7) : 
    getCurrentMonths(Math.min(monthCount, 1)); // Limit to current month for now
  
  // Filter out today's date to prevent showing fake data for today
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const filteredPeriods = limitedPeriods.filter(period => period !== today);
  
  console.log(`📊 Generated periods (excluding today): ${filteredPeriods.join(', ')}`);
  
  return filteredPeriods.map((period, index) => ({
    name: period,
    value: Math.max(0.01, baseValue + (Math.random() * variation - variation/2) + (index * trend))
  }));
}

function generateMonthlyHistory(currentValue: number, type: 'engagement' | 'growth', monthCount: number = 6, platformData?: any, isDailyView: boolean = false) {
  // Check if there's no activity at all
  const followerCount = platformData?.followers || platformData?.follower_count || 0;
  const postsCount = platformData?.total_posts_analyzed || 0;
  
  if (followerCount === 0 && postsCount === 0) {
    console.log(`📊 No activity detected for ${type} history - returning empty data`);
    return []; // Return empty array - no fake data
  }
  
  const isNewAccount = followerCount <= 5 && postsCount <= 10;
  
  if (isNewAccount) {
    console.log(`📊 New account detected for ${type} history - showing minimal current data only`);
    
    // For new accounts, only show yesterday (not today)
    if (isDailyView) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return [
        {
          name: yesterday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          value: type === 'engagement' ? 0.1 : 0.05
        }
      ];
    } else {
      const currentMonth = getCurrentMonths(1);
      return currentMonth.map((month) => ({
        name: month,
        value: type === 'engagement' ? 0.1 : 0.05
      }));
    }
  }
  
  // For established accounts, still limit to reasonable data periods
  // Don't generate fake historical data for months we don't have
  let baseValue: number;
  let variation: number;
  let growthTrend: number;
  
  if (type === 'engagement') {
    if (currentValue < 1) {
      baseValue = 0.5;
      variation = 0.3;
      growthTrend = 0.05;
    } else {
      baseValue = Math.max(0.5, currentValue * 0.85);
      variation = Math.max(0.2, Math.abs(currentValue * 0.3));
      growthTrend = Math.max(0.02, currentValue * 0.03);
    }
  } else {
    if (currentValue < 0.5) {
      baseValue = 0.1;
      variation = 0.2;
      growthTrend = 0.03;
    } else {
      baseValue = Math.max(0.1, currentValue * 0.85);
      variation = Math.max(0.1, Math.abs(currentValue * 0.3));
      growthTrend = Math.max(0.01, currentValue * 0.03);
    }
  }
  
  // Limit periods to avoid showing fake historical data
  const limitedPeriods = isDailyView ? 
    getCurrentDays(7) : 
    getCurrentMonths(Math.min(monthCount, 1)); // Limit to current month for now
  
  // Filter out today's date to prevent showing fake data for today
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const filteredPeriods = limitedPeriods.filter(period => period !== today);
  
  console.log(`📊 Monthly history periods (excluding today): ${filteredPeriods.join(', ')}`);
  
  return filteredPeriods.map((period, index) => ({
    name: period,
    value: Math.max(0.01, baseValue + (Math.random() * variation - variation/2) + (index * growthTrend))
  }));
}

export const fetchAudienceAnalytics = async (dateRange: '1m' | '3m' | '6m' | '1y' = '1m', platform?: string): Promise<AudienceAnalyticsData> => {
  const days = getMonthCountFromRange(dateRange) * 30;
  
  console.log(`📊 fetchAudienceAnalytics called with dateRange: ${dateRange}, platform: ${platform}, days: ${days}`);
  
  try {
    // Try to fetch real audience analytics data
    console.log('📊 Attempting to fetch real audience analytics data...');
    const url = platform ? `/social/audience-analytics?days=${days}&platform=${platform}` : `/social/audience-analytics?days=${days}`;
    const response = await api.get(url);
    const audienceData = response.data;
    console.log('📊 Raw audience data received:', audienceData);
    
    if (audienceData.platforms && audienceData.platforms.length > 0) {
      // Use comprehensive real data from connected platforms
      return {
        demographics: audienceData.demographics,
        activeHours: audienceData.active_hours,
        sentimentBreakdown: audienceData.sentiment_breakdown,
        audienceDensityByCountry: audienceData.audience_by_country,
        platformSentimentBreakdown: audienceData.platform_sentiment_breakdown || audienceData.platforms.map((platform: any) => ({
          platform: platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1),
          data: [
            { name: 'Positive', value: platform.sentiment_breakdown?.positive || 0 },
            { name: 'Neutral', value: platform.sentiment_breakdown?.neutral || 0 },
            { name: 'Negative', value: platform.sentiment_breakdown?.negative || 0 }
          ]
        })),
        platformAudienceDensityByCountry: audienceData.platforms?.map((platform: any) => ({
          platform: platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1),
          data: platform.audience_by_country || [{ country: 'Global', value: 100 }]
        })) || audienceData.platform_audience_density_by_country || [
          { platform: 'Instagram', data: [{ country: 'Global', value: 100 }] },
          { platform: 'Facebook', data: [{ country: 'Global', value: 100 }] }
        ],
        // Include new comprehensive metrics
        platforms: audienceData.platforms,
        combined_metrics: audienceData.combined_metrics,
        summary: audienceData.summary
      };
    }
  } catch (error) {
    console.error('📊 Error fetching real audience analytics data:', error);
    console.log('📊 Using fallback audience data - no connected accounts or API error');
  }
  
  // Fallback to mock data
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        demographics: {
          age: '25-34',
          gender: 'Female',
          location: 'New York, USA',
        },
        activeHours: [
          { hour: '00:00', engagement: 10 },
          { hour: '01:00', engagement: 8 },
          { hour: '02:00', engagement: 5 },
          { hour: '03:00', engagement: 3 },
          { hour: '04:00', engagement: 2 },
          { hour: '05:00', engagement: 5 },
          { hour: '06:00', engagement: 15 },
          { hour: '07:00', engagement: 25 },
          { hour: '08:00', engagement: 40 },
          { hour: '09:00', engagement: 60 },
          { hour: '10:00', engagement: 75 },
          { hour: '11:00', engagement: 85 },
          { hour: '12:00', engagement: 90 },
          { hour: '13:00', engagement: 88 },
          { hour: '14:00', engagement: 80 },
          { hour: '15:00', engagement: 70 },
          { hour: '16:00', engagement: 60 },
          { hour: '17:00', engagement: 50 },
          { hour: '18:00', engagement: 45 },
          { hour: '19:00', engagement: 40 },
          { hour: '20:00', engagement: 35 },
          { hour: '21:00', engagement: 30 },
          { hour: '22:00', engagement: 25 },
          { hour: '23:00', engagement: 18 },
        ],
        sentimentBreakdown: {
          positive: 75,
          neutral: 15,
          negative: 10,
        },
        audienceDensityByCountry: [
            { country: 'USA', value: 40 },
            { country: 'India', value: 20 },
            { country: 'Brazil', value: 15 },
            { country: 'Germany', value: 10 },
            { country: 'Japan', value: 8 },
            { country: 'Other', value: 7 },
        ],
        platformSentimentBreakdown: [
            { platform: 'Facebook', data: [{ name: 'Positive', value: 70 }, { name: 'Neutral', value: 20 }, { name: 'Negative', value: 10 }] },
            { platform: 'Instagram', data: [{ name: 'Positive', value: 80 }, { name: 'Neutral', value: 15 }, { name: 'Negative', value: 5 }] },
            { platform: 'Twitter', data: [{ name: 'Positive', value: 60 }, { name: 'Neutral', value: 25 }, { name: 'Negative', value: 15 }] },
            { platform: 'LinkedIn', data: [{ name: 'Positive', value: 85 }, { name: 'Neutral', value: 10 }, { name: 'Negative', value: 5 }] },
            { platform: 'TikTok', data: [{ name: 'Positive', value: 75 }, { name: 'Neutral', value: 10 }, { name: 'Negative', value: 15 }] },
            { platform: 'YouTube', data: [{ name: 'Positive', value: 90 }, { name: 'Neutral', value: 5 }, { name: 'Negative', value: 5 }] },
        ],
        platformAudienceDensityByCountry: [
            { platform: 'Facebook', data: [{ country: 'USA', value: 30 }, { country: 'India', value: 25 }, { country: 'Brazil', value: 20 }, { country: 'Other', value: 25 }] },
            { platform: 'Instagram', data: [{ country: 'USA', value: 45 }, { country: 'India', value: 15 }, { country: 'Germany', value: 10 }, { country: 'Other', value: 30 }] },
            { platform: 'Twitter', data: [{ country: 'USA', value: 35 }, { country: 'Japan', value: 20 }, { country: 'Brazil', value: 10 }, { country: 'Other', value: 35 }] },
            { platform: 'LinkedIn', data: [{ country: 'USA', value: 50 }, { country: 'India', value: 10 }, { country: 'Germany', value: 15 }, { country: 'Other', value: 25 }] },
            { platform: 'TikTok', data: [{ country: 'USA', value: 25 }, { country: 'Brazil', value: 30 }, { country: 'Japan', value: 15 }, { country: 'Other', value: 30 }] },
            { platform: 'YouTube', data: [{ country: 'USA', value: 40 }, { country: 'India', value: 20 }, { country: 'Germany', value: 10 }, { country: 'Other', value: 30 }] },
        ],
      });
    }, 500);
  });
};
